import { useState } from "react";
import { PlaygroundImage } from "../../../../models/playground";
import { TextToImageConfigType } from "../../../../models/text-to-image";
import { ExampleType } from "../CodeExamplesDropdown/CodeExamplesDropdown";
import { Language } from "../LanguagesDropdown/LanguagesDropdown";
import {
	codeGenerators,
	extractImageParams,
	generateGeneratePayload,
	generateRefinePayload,
	generateRegeneratePayload,
} from "../utils/codeGenerators";

export const useCodeGeneration = (image: PlaygroundImage) => {
	const [selectedExample, setSelectedExample] = useState<ExampleType>("generate");
	const [selectedLanguage, setSelectedLanguage] = useState<Language>("python");

	const generateCodeExample = (): string => {
		const version = (image.config as TextToImageConfigType)?.model_version || "";
		const baseUrl = `https://engine.prod.bria-api.com/v2/image/generate${version ? `/${version}` : ""}`;
		const apiToken = "YOUR_API_TOKEN";
		const imageParams = extractImageParams(image);

		let payload: Record<string, any>;

		switch (selectedExample) {
			case "regenerate":
				payload = generateRegeneratePayload(imageParams);
				break;
			case "generate":
				const prompt = getGeneratePrompt();
				payload = generateGeneratePayload(imageParams, prompt);
				break;
			case "refine":
				const refinePrompt = getRefinePrompt();
				payload = generateRefinePayload(imageParams, refinePrompt);
				break;
			default:
				payload = generateRegeneratePayload(imageParams);
		}

		const generator = codeGenerators[selectedLanguage];
		return generator ? generator(payload, baseUrl, apiToken) : "";
	};

	const getGeneratePrompt = (): string => {
		const isFromRefine = image.config && (image.config as any).is_refine;

		if (isFromRefine) {
			return getShortDescriptionFromStructuredPrompt() || "";
		}

		return (image.config as any)?.prompt;
	};

	const getRefinePrompt = (): string => {
		const isFromRefine = image.config && (image.config as any).is_refine;

		return isFromRefine
			? (image.config as any)?.prompt || "Transform the visual style into a vibrant, impressionist oil painting."
			: "Transform the visual style into a vibrant, impressionist oil painting.";
	};

	const getShortDescriptionFromStructuredPrompt = (): string | null => {
		try {
			const structuredPrompt = (image.config as TextToImageConfigType)?.structured_prompt;
			if (!structuredPrompt) return null;

			const parsed = typeof structuredPrompt === "string" ? JSON.parse(structuredPrompt) : structuredPrompt;

			return parsed?.short_description || null;
		} catch {
			return null;
		}
	};

	return {
		selectedExample,
		setSelectedExample,
		selectedLanguage,
		setSelectedLanguage,
		generateCodeExample,
	};
};
